/**
 * sl-hihx.2.2: Marketplace Actions Hook
 *
 * Provides install (via frontend ZipTransformer), rate, and report actions
 * for marketplace templates.
 *
 * SOLID:
 * - S: Only action dispatching, no UI state beyond progress tracking
 */
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { DocsService } from '@affine/core/modules/doc';
import { GuardService } from '@affine/core/modules/permissions';
import {
  getStreamLainWorkspaceSchema,
  WorkspaceService,
} from '@affine/core/modules/workspace';
import {
  DOC_SIZE_LIMIT_BYTES,
  formatDocumentSizeScore,
} from '@affine/core/modules/workspace/views/document-size-score';
import { useI18n } from '@affine/i18n';
import type { RootBlockModel } from '@blocksuite/affine/model';
import { replaceIdMiddleware } from '@blocksuite/affine/shared/adapters';
import { Slice, type Store, Transformer } from '@blocksuite/affine/store';
import {
  createStreamlainIdTracker,
  remapStreamlainReferences,
  ZipTransformer,
} from '@blocksuite/affine/widgets/linked-doc';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useRef, useState } from 'react';
import { encodeStateAsUpdate } from 'yjs';

import {
  applyAutomationDatabaseBindingImportOptions,
  resolveAutomationDatabaseBindings,
} from './automation-database-binding-wiring';
import {
  type AutomationDatabaseBindings,
  buildColumnMappings,
  hasExistingDatabaseBindings,
} from './automation-database-bindings';
import {
  buildImportedMarketplaceDatabaseDocIdReplacements,
  type DatabaseSystemDocAccessBinding,
  registerImportedMarketplaceDatabaseSystemDocAccess,
  registerMarketplaceDatabaseSystemDocAccessBindings,
  repairImportedMarketplaceDatabaseReferences,
} from './automation-database-import-wiring';
import { applyAutomationInstallOptions } from './automation-install-options';
import {
  getAutomationBlockIdsFromSlice,
  wireInstalledAutomationArtifactBindings,
  wireInstalledAutomationBlocks,
} from './automation-post-import-wiring';
import {
  completeInstallWithRecovery,
  forgetPendingCompositeInstall,
  readPendingCompositeInstall,
  recoverCompositeInstallResponse,
  rememberPendingCompositeInstall,
} from './composite-install-recovery';
import type { ConcretizationBindings } from './import-setup-wizard';
import { createMarketplaceActionError } from './marketplace-error';
import { publishMarketplaceInstallChange } from './marketplace-install-state';
import {
  placeMarketplaceTemplateSnapshotForAppend,
  removeMergedMarketplaceDocLinks,
} from './marketplace-template-placement';
import type { CredentialRequirement } from './use-marketplace-api';

// =============================================================================
// Types
// =============================================================================

export interface InstallResult {
  installId?: string;
  installedDocIds: string[];
  workflowId: string | null;
  templateTitle: string;
  credentialRequirements: CredentialRequirement[];
  appInstall?: {
    rootDocId: string;
    customAppId: string;
    draftVersion: number;
    automationBindings?: Array<{
      actionId: string;
      blockId: string;
      workflowId: string;
    }>;
  };
  artifactBindings?: {
    schemaVersion: 1;
    bindings: Array<{
      sourceBlockId?: string;
      targetDocId?: string;
      targetBlockId?: string;
      workflowId?: string;
    }>;
  };
}

export interface RatingResult {
  id: string;
  templateId: string;
  userId: string;
  rating: number;
  comment: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReportResult {
  id: string;
  templateId: string;
  userId: string;
  reason: string;
  details: string | null;
  status: string;
  createdAt: string;
}

interface InstallableTemplateSummary {
  templateType:
    | 'doc'
    | 'app'
    | 'app_block'
    | 'step'
    | 'workflow'
    | 'automation';
  stepInsertType?: string | null;
}

interface StoreClientWithDocLookup {
  waitForLocalDocStorageConnected?: () => Promise<void>;
  getDoc?: (
    docId: string
  ) => Promise<{ bin?: { byteLength?: number; length?: number } } | null>;
}

type WorkspaceWithDocSyncFlush = {
  id: string;
  engine: {
    doc: {
      flushAndWaitForSynced?: (docId: string) => Promise<void>;
    };
  };
};

type MarketplaceDocsServiceSync = Pick<
  DocsService,
  'isLocallyCreated' | 'waitForServerConfirmed'
>;

type YDocArchiveImportTarget = {
  readonly sourceDocId?: string;
  readonly targetDocId: string;
  readonly system: boolean;
  readonly blockIdReplacements?: readonly {
    readonly sourceBlockId: string;
    readonly targetBlockId: string;
  }[];
  readonly databaseSystemDocAccess?: readonly DatabaseSystemDocAccessBinding[];
};

export type InstallStep =
  | 'preparing'
  | 'importing-docs'
  | 'creating-workflow'
  | 'finalizing'
  | 'done'
  | 'error';

/** Where to open imported docs after install. */
export type InstallTarget = 'new-doc' | 'stay';

function getImportedDocTitle(doc: Store): string | undefined {
  const rootTitle = (
    doc.getBlocksByFlavour('affine:page').at(0)?.model as
      | RootBlockModel
      | undefined
  )?.props.title
    .toString()
    .trim();
  if (rootTitle) return rootTitle;

  const metaTitle = doc.meta?.title?.trim();
  return metaTitle || undefined;
}

function getEncodedStoreSize(doc: Store): number {
  return encodeStateAsUpdate(doc.spaceDoc).byteLength;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

async function readLocalDocSize(
  client: StoreClientWithDocLookup,
  docId: string
): Promise<number> {
  if (!client.getDoc) return 0;
  await client.waitForLocalDocStorageConnected?.();
  const record = await client.getDoc(docId);
  return record?.bin?.byteLength ?? record?.bin?.length ?? 0;
}

function syncImportedUserVisibleDocMetadata(
  docsService: DocsService,
  docs: readonly Store[]
): void {
  for (const doc of docs) {
    const title = getImportedDocTitle(doc);
    docsService.notifyDocCreated(
      doc.id,
      title ? { title, updatedDate: Date.now() } : undefined
    );
  }
}

async function waitForMarketplaceTargetDocRoot(
  targetDoc: Store,
  options: { timeoutMs?: number; pollMs?: number } = {}
): Promise<boolean> {
  const timeoutMs = options.timeoutMs ?? 5_000;
  const pollMs = options.pollMs ?? 50;
  const deadline = Date.now() + timeoutMs;

  while (!targetDoc.root && Date.now() < deadline) {
    await sleep(pollMs);
  }

  return !!targetDoc.root;
}

async function flushMarketplaceInstalledDocs(
  workspace: WorkspaceWithDocSyncFlush,
  docs: readonly Store[]
): Promise<void> {
  if (!workspace.engine.doc.flushAndWaitForSynced) return;

  const docIds = [...new Set(docs.map(doc => doc.id))];
  for (const docId of docIds) {
    await workspace.engine.doc.flushAndWaitForSynced(docId);
  }
}

async function preflightMarketplaceImportDocs(input: {
  workspaceId: string;
  targets: readonly YDocArchiveImportTarget[];
  appendTargetDocId: string | null;
  signal: AbortSignal;
}): Promise<void> {
  if (input.targets.length === 0) return;

  const databaseSystemDocAccess = input.targets.flatMap(
    target => target.databaseSystemDocAccess ?? []
  );
  const serverDatabaseSystemDocIdPrefix = `db$${input.workspaceId}$database$`;
  const pageDocIds = input.appendTargetDocId
    ? []
    : input.targets
        .filter(target => !target.system)
        .map(target => target.targetDocId);
  const ephemeralPageDocIds = input.appendTargetDocId
    ? input.targets
        .filter(target => !target.system)
        .map(target => target.targetDocId)
    : [];
  const systemDocIds = [
    ...new Set([
      ...input.targets
        .filter(
          target =>
            target.system &&
            target.targetDocId.startsWith(serverDatabaseSystemDocIdPrefix)
        )
        .map(target => target.targetDocId),
      ...databaseSystemDocAccess.flatMap(binding => [
        binding.rootDocId,
        ...binding.manifest.shards.map(shard => shard.serverDocId),
      ]),
    ]),
  ].filter(
    (docId): docId is string =>
      typeof docId === 'string' &&
      docId.startsWith(serverDatabaseSystemDocIdPrefix)
  );

  const response = await workspaceApiFetch(
    `/api/workspace/${input.workspaceId}/marketplace/import-docs/preflight`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pageDocIds,
        ephemeralPageDocIds,
        systemDocIds,
      }),
      signal: input.signal,
    }
  );

  if (!response.ok) {
    throw new Error(
      await response.text().catch(() => 'Marketplace import preflight failed')
    );
  }

  await registerMarketplaceDatabaseSystemDocAccessBindings(
    input.workspaceId,
    databaseSystemDocAccess,
    input.signal
  );
}

async function discardMarketplaceImportPlaceholders(input: {
  workspaceId: string;
  docIds: readonly string[];
  signal: AbortSignal;
}): Promise<void> {
  if (input.docIds.length === 0) return;

  const response = await workspaceApiFetch(
    `/api/workspace/${input.workspaceId}/marketplace/import-docs/discard-placeholders`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ docIds: [...new Set(input.docIds)] }),
      signal: input.signal,
    }
  );

  if (!response.ok) {
    throw new Error(
      await response
        .text()
        .catch(() => 'Marketplace import placeholder cleanup failed')
    );
  }
}

async function waitForImportedDocsServerConfirmed(
  workspace: WorkspaceWithDocSyncFlush,
  docsService: MarketplaceDocsServiceSync,
  docs: readonly Store[]
): Promise<void> {
  const pendingDocIds = [
    ...new Set(
      docs
        .map(doc => doc.id)
        .filter(docId => docsService.isLocallyCreated(docId))
    ),
  ];
  if (pendingDocIds.length === 0) return;

  if (workspace.engine.doc.flushAndWaitForSynced) {
    await workspace.engine.doc.flushAndWaitForSynced(workspace.id);
    for (const docId of pendingDocIds) {
      await workspace.engine.doc.flushAndWaitForSynced(docId);
    }
    await workspace.engine.doc.flushAndWaitForSynced(workspace.id);
  }

  for (const docId of pendingDocIds) {
    const confirmed = await docsService.waitForServerConfirmed(docId, {
      timeoutMs: 15_000,
      pollMs: 250,
    });
    if (!confirmed) {
      throw new Error(
        `Imported document ${docId} was not confirmed by the server`
      );
    }
  }
}

function toInstallErrorMessage(
  err: unknown,
  t: ReturnType<typeof useI18n>
): string {
  const code =
    err instanceof Error ? (err as Error & { code?: string }).code : undefined;
  if (code === 'MARKETPLACE_APP_BLOCK_TARGET_APP_MANAGE_DENIED') {
    return t[
      'com.streamlain.customApps.customAppBuilder.wizard.appBlock.error.manageDenied'
    ]();
  }
  const message = err instanceof Error ? err.message : '';
  if (!message) {
    return t['com.streamlain.marketplace.import.confirm.error.generic']();
  }

  const technicalErrorPattern =
    /(^com\.streamlain\.error\.|Cannot read properties|is not a function|TypeError|ReferenceError|SyntaxError|statusCode|Internal server error|Failed to (load|download|complete|register|import)|Failed to commit marketplace install transaction|was not confirmed|Automation install did not return|Installation failed)/i;

  return technicalErrorPattern.test(message)
    ? t['com.streamlain.marketplace.import.confirm.error.generic']()
    : message;
}

// =============================================================================
// Hook: useMarketplaceActions
// =============================================================================

export function useMarketplaceActions(workspaceId: string | undefined) {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const docsService = useService(DocsService);
  const guardService = useService(GuardService);

  // Install state
  const [installStep, setInstallStep] = useState<InstallStep | null>(null);
  const [installResult, setInstallResult] = useState<InstallResult | null>(
    null
  );
  const [installError, setInstallError] = useState<string | null>(null);
  const installAbortRef = useRef<AbortController | null>(null);
  const [uninstallingId, setUninstallingId] = useState<string | null>(null);
  const [uninstallError, setUninstallError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId || !workspace) return;
    const pending = readPendingCompositeInstall(workspaceId);
    if (!pending) return;
    const controller = new AbortController();
    const releases: Array<() => void> = [];
    void (async () => {
      const response = await recoverCompositeInstallResponse({
        workspaceId,
        installRequestId: pending.installRequestId,
        signal: controller.signal,
        fetcher: workspaceApiFetch,
      });
      if (!response) return;
      const recovery = (await response.json()) as InstallResult & {
        templateId: string;
      };
      const bindings = recovery.artifactBindings?.bindings ?? [];
      if (bindings.length > 0) {
        const targetDocIds = [
          ...new Set(
            bindings
              .map(binding => binding.targetDocId)
              .filter((id): id is string => typeof id === 'string' && !!id)
          ),
        ];
        const docs: Store[] = [];
        for (const docId of targetDocIds) {
          const { release, doc } = docsService.open(docId);
          releases.push(release);
          await doc.waitForSyncReady();
          docs.push(doc.blockSuiteDoc);
        }
        wireInstalledAutomationArtifactBindings(docs, bindings);
        await flushMarketplaceInstalledDocs(workspace, docs);
        if (workspace.engine.doc.flushAndWaitForSynced) {
          await workspace.engine.doc.flushAndWaitForSynced(workspace.id);
        }
        for (const docId of targetDocIds) {
          const confirmed = await docsService.waitForServerConfirmed(docId, {
            timeoutMs: 15_000,
            pollMs: 250,
          });
          if (!confirmed) {
            throw new Error(
              `Recovered marketplace document ${docId} was not confirmed by the server`
            );
          }
        }
      }
      forgetPendingCompositeInstall(workspaceId);
    })()
      .catch(error => {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          console.warn(
            '[Marketplace] Pending composite install recovery failed',
            error
          );
        }
      })
      .finally(() => {
        for (const release of releases) release();
      });
    return () => controller.abort();
  }, [docsService, workspace, workspaceId]);

  const installTemplate = useCallback(
    async (
      templateId: string,
      _target: InstallTarget = 'new-doc',
      bindings?: ConcretizationBindings,
      // Install-time options collected by the wizard. `targetVersionNumber`
      // must be sent to both archive download and complete-install so the
      // backend records exactly the version whose ZIP the client imported.
      installOptions?: {
        targetVersionNumber?: number;
        targetDocId?: string;
        createInputDbs?: boolean;
        createOutputDbs?: boolean;
        databaseBindings?: AutomationDatabaseBindings;
        appBlockTarget?:
          | {
              operationId: string;
              kind: 'existing_app';
              customAppId: string;
            }
          | {
              operationId: string;
              kind: 'new_app';
              title: string;
              appKind: 'workspace';
            };
        appBlockTargetRootRoutePath?: string;
        appBlockDatabaseMappingDecisions?: Record<
          string,
          { action: 'create_new' } | { action: 'use_existing'; target: string }
        >;
        appBlockConfirmedPreflightDigest?: string;
      },
      templateSummaryInput?: InstallableTemplateSummary,
      actionOptions?: {
        replaceInstallId?: string;
        propagateError?: boolean;
      }
    ): Promise<InstallResult | null> => {
      if (!workspaceId || !workspace) return null;

      installAbortRef.current?.abort();
      const controller = new AbortController();
      installAbortRef.current = controller;

      setInstallStep('preparing');
      setInstallResult(null);
      setInstallError(null);

      let backendInstallRecorded = false;
      let committedInstallId: string | null = null;
      const cleanupImportedDocIdsAfterBackendFailure = new Set<string>();
      try {
        let templateSummary = templateSummaryInput;
        if (!templateSummary) {
          const detailRes = await workspaceApiFetch(
            `/api/workspace/${workspaceId}/marketplace/templates/${templateId}`,
            {
              credentials: 'include',
              signal: controller.signal,
            }
          );
          if (detailRes.ok) {
            templateSummary =
              (await detailRes.json()) as InstallableTemplateSummary;
          } else {
            const globalRes = await fetch(
              `/api/marketplace/templates/${templateId}`,
              {
                credentials: 'include',
                signal: controller.signal,
              }
            );
            if (!globalRes.ok) {
              const body = await globalRes.json().catch(() => ({}));
              throw new Error(
                body.message ?? `Failed to load template: ${globalRes.status}`
              );
            }
            templateSummary =
              (await globalRes.json()) as InstallableTemplateSummary;
          }
        }
        const isManagedStep =
          templateSummary.templateType === 'step' &&
          ['managed_code', 'managed_http', 'managed_ai'].includes(
            templateSummary.stepInsertType ?? ''
          );
        const isCatalogAppBlock = templateSummary.templateType === 'app_block';

        if (isCatalogAppBlock) {
          const target = installOptions?.appBlockTarget;
          if (!target) {
            throw new Error(
              'App Block installation requires an explicit target App'
            );
          }
          setInstallStep('finalizing');
          const endpoint = `/api/workspace/${workspaceId}/marketplace/templates/${templateId}`;
          const requestTarget =
            target.kind === 'existing_app'
              ? {
                  kind: 'existing_app' as const,
                  customAppId: target.customAppId,
                }
              : {
                  kind: 'new_app' as const,
                  title: target.title,
                  appKind: target.appKind,
                };
          if (!installOptions?.appBlockConfirmedPreflightDigest) {
            throw new Error(
              'App Block installation requires a confirmed visible preflight'
            );
          }
          const response = await workspaceApiFetch(
            `${endpoint}/app-block-instantiate`,
            {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                operationId: target.operationId,
                confirmedPreflightDigest:
                  installOptions.appBlockConfirmedPreflightDigest,
                target: requestTarget,
                targetRootRoutePath:
                  installOptions.appBlockTargetRootRoutePath ?? '/',
                targetVersionNumber: installOptions?.targetVersionNumber,
                ...(installOptions.appBlockDatabaseMappingDecisions
                  ? {
                      databaseMappingDecisions:
                        installOptions.appBlockDatabaseMappingDecisions,
                    }
                  : {}),
                ...(bindings ? { bindings } : {}),
              }),
              signal: controller.signal,
            },
            { workspaceId, mode: 'foreground' }
          );
          if (!response.ok) {
            throw createMarketplaceActionError(
              await response.json().catch(() => ({})),
              `App Block installation failed: ${response.status}`
            );
          }
          const installed = (await response.json()) as {
            installReceiptId: string;
            installedDocIds: string[];
            customAppId: string;
            draftVersion: number;
          };
          const result: InstallResult = {
            installId: installed.installReceiptId,
            installedDocIds: installed.installedDocIds,
            workflowId: null,
            templateTitle: '',
            credentialRequirements: [],
            appInstall: {
              rootDocId: installed.installedDocIds[0] ?? '',
              customAppId: installed.customAppId,
              draftVersion: installed.draftVersion,
            },
          };
          setInstallStep('done');
          setInstallResult(result);
          publishMarketplaceInstallChange({
            kind: actionOptions?.replaceInstallId ? 'updated' : 'installed',
            workspaceId,
            templateId,
          });
          return result;
        }

        let installedDocIds: string[] = [];
        let keptImportedDocs: Store[] = [];
        let docIdReplacements: ReadonlyMap<string, string> | undefined;
        const importDocIdReplacements = new Map<string, string>();
        const importBlockIdReplacements = new Map<string, string>();
        let databaseDocIdReplacements = new Map<string, string>();
        let resolvedDatabaseBindings: AutomationDatabaseBindings | undefined;
        let automationBlockIdsToWire: string[] | undefined;
        let effectiveBindings = bindings;
        if (!isManagedStep && !isCatalogAppBlock) {
          const versionQuery =
            installOptions?.targetVersionNumber != null
              ? `?version=${encodeURIComponent(
                  String(installOptions.targetVersionNumber)
                )}`
              : '';
          // 1. Download template ZIP from backend
          const zipRes = await workspaceApiFetch(
            `/api/workspace/${workspaceId}/marketplace/templates/${templateId}/download-zip${versionQuery}`,
            {
              credentials: 'include',
              signal: controller.signal,
            }
          );

          if (!zipRes.ok) {
            const body = await zipRes.json().catch(() => ({}));
            throw new Error(
              body.message ?? `Failed to download template: ${zipRes.status}`
            );
          }

          setInstallStep('importing-docs');

          // 2. Import docs via ZipTransformer, then repair StreamLain-owned
          //    database refs for globally published marketplace templates.
          const zipBlob = await zipRes.blob();
          const importedDocs = await ZipTransformer.importDocs(
            workspace.docCollection,
            getStreamLainWorkspaceSchema(),
            zipBlob,
            {
              beforeYDocArchiveDocsLoaded: targets => {
                for (const target of targets) {
                  if (target.sourceDocId) {
                    importDocIdReplacements.set(
                      target.sourceDocId,
                      target.targetDocId
                    );
                  }
                  for (const replacement of target.blockIdReplacements ?? []) {
                    importBlockIdReplacements.set(
                      replacement.sourceBlockId,
                      replacement.targetBlockId
                    );
                  }
                }
                return preflightMarketplaceImportDocs({
                  workspaceId,
                  targets,
                  appendTargetDocId: installOptions?.targetDocId ?? null,
                  signal: controller.signal,
                });
              },
              transientPageDocs: Boolean(installOptions?.targetDocId),
            }
          );

          const validDocs: Store[] = importedDocs.filter(
            (doc: Store | undefined): doc is Store => doc != null
          );

          repairImportedMarketplaceDatabaseReferences(validDocs, workspaceId);
          databaseDocIdReplacements =
            buildImportedMarketplaceDatabaseDocIdReplacements(
              validDocs,
              workspaceId
            );
          normalizeImportedDocIdReplacementValues(
            importDocIdReplacements,
            databaseDocIdReplacements
          );

          // sl-67kc.4.1: For Level-3 (automation) installs, honor the user's
          // database opt-out choice by removing the imported input/output
          // DB docs before they are surfaced in the UI. Runs before
          // `notifyDocCreated` so opted-out DBs never flash into All Docs.
          let removedDocIds: readonly string[] = [];
          if (hasExistingDatabaseBindings(installOptions?.databaseBindings)) {
            removedDocIds = applyAutomationDatabaseBindingImportOptions(
              validDocs,
              installOptions?.databaseBindings,
              docId => workspace.docCollection.removeDoc(docId)
            );
          } else if (
            installOptions &&
            (installOptions.createInputDbs === false ||
              installOptions.createOutputDbs === false)
          ) {
            removedDocIds = applyAutomationInstallOptions(
              validDocs,
              {
                targetDocId: installOptions.targetDocId ?? null,
                createInputDbs: installOptions.createInputDbs ?? true,
                createOutputDbs: installOptions.createOutputDbs ?? true,
              },
              docId => workspace.docCollection.removeDoc(docId)
            );
          }

          let keptDocs = validDocs.filter(
            (d: Store) => !removedDocIds.includes(d.id)
          );
          let userVisibleDocs = keptDocs.filter(
            (doc: Store) => !isMarketplaceSystemDocId(doc.id)
          );

          if (installOptions?.targetDocId) {
            const targetDocId = installOptions.targetDocId;
            const systemDocs = keptDocs.filter((doc: Store) =>
              isMarketplaceSystemDocId(doc.id)
            );
            const importedPageDocs = keptDocs.filter(
              (doc: Store) => !isMarketplaceSystemDocId(doc.id)
            );
            const appendDocIdReplacements = new Map(
              importedPageDocs.map(doc => [doc.id, targetDocId])
            );
            const appendBlockIdReplacements = new Map<string, string>();
            docIdReplacements = appendDocIdReplacements;
            const importedPageDocIds = new Set(
              importedPageDocs.map(doc => doc.id)
            );

            try {
              const canWrite =
                workspace.flavour === 'local'
                  ? true
                  : await guardService
                      .can('Doc_Update', targetDocId)
                      .catch(() => false);
              if (!canWrite) {
                throw new Error(
                  t[
                    'com.streamlain.marketplace.import.target.error.permission'
                  ]()
                );
              }

              const { release: targetRelease, doc: targetDocEntity } =
                docsService.open(targetDocId);
              try {
                await targetDocEntity.waitForSyncReady();
                const collection = workspace.docCollection;
                const targetDoc = targetDocEntity.blockSuiteDoc;
                if (!(await waitForMarketplaceTargetDocRoot(targetDoc))) {
                  throw new Error(
                    t[
                      'com.streamlain.marketplace.import.target.error.missing'
                    ]()
                  );
                }
                if (templateSummary.templateType === 'automation') {
                  automationBlockIdsToWire = [];
                }
                resolvedDatabaseBindings = resolveAutomationDatabaseBindings(
                  targetDoc,
                  installOptions?.databaseBindings
                );
                if (resolvedDatabaseBindings) {
                  const columnMappings = buildColumnMappings(
                    resolvedDatabaseBindings
                  );
                  effectiveBindings = {
                    ...(effectiveBindings ?? {
                      credentialMappings: {},
                      configValues: {},
                    }),
                    columnMappings: {
                      ...effectiveBindings?.columnMappings,
                      ...columnMappings,
                    },
                  };
                }

                const client = workspace.engine
                  .client as StoreClientWithDocLookup;
                const persistedTargetSize = await readLocalDocSize(
                  client,
                  targetDocId
                );
                const targetSize = Math.max(
                  persistedTargetSize,
                  getEncodedStoreSize(targetDoc)
                );
                const importedSize = importedPageDocs.reduce(
                  (total, doc) => total + getEncodedStoreSize(doc),
                  0
                );

                if (targetSize + importedSize >= DOC_SIZE_LIMIT_BYTES) {
                  throw new Error(
                    t['com.streamlain.marketplace.import.target.error.size']({
                      limit: formatDocumentSizeScore(DOC_SIZE_LIMIT_BYTES),
                    })
                  );
                }

                const transformer = new Transformer({
                  schema: getStreamLainWorkspaceSchema(),
                  blobCRUD: collection.blobSync,
                  docCRUD: {
                    create: (id: string) =>
                      collection.createDoc(id).getStore({ id }),
                    get: (id: string) =>
                      collection.getDoc(id)?.getStore({ id }) ?? null,
                    delete: (id: string) => collection.removeDoc(id),
                  },
                  middlewares: [
                    createStreamlainIdTracker(
                      appendDocIdReplacements,
                      appendBlockIdReplacements
                    ),
                    replaceIdMiddleware(collection.idGenerator),
                  ],
                });

                for (const importedDoc of importedPageDocs) {
                  const children = importedDoc.root?.children ?? [];
                  if (children.length === 0) continue;
                  const snapshot = transformer.sliceToSnapshot(
                    Slice.fromModels(importedDoc, [...children])
                  );
                  if (!snapshot) {
                    throw new Error(
                      t[
                        'com.streamlain.marketplace.import.target.error.append'
                      ]()
                    );
                  }
                  placeMarketplaceTemplateSnapshotForAppend(
                    snapshot,
                    targetDoc
                  );
                  removeMergedMarketplaceDocLinks(snapshot, importedPageDocIds);
                  if (snapshot.content.length === 0) continue;
                  const targetRootId = targetDoc.root?.id;
                  if (!targetRootId) {
                    throw new Error(
                      t[
                        'com.streamlain.marketplace.import.target.error.append'
                      ]()
                    );
                  }
                  const importedSlice = await transformer.snapshotToSlice(
                    snapshot,
                    targetDoc,
                    targetRootId
                  );
                  if (!importedSlice) {
                    throw new Error(
                      t[
                        'com.streamlain.marketplace.import.target.error.append'
                      ]()
                    );
                  }
                  automationBlockIdsToWire?.push(
                    ...getAutomationBlockIdsFromSlice(importedSlice)
                  );
                }

                remapStreamlainReferences(
                  [targetDoc],
                  appendDocIdReplacements,
                  appendBlockIdReplacements
                );
                composeReplacementMap(
                  importDocIdReplacements,
                  appendDocIdReplacements
                );
                composeReplacementMap(
                  importBlockIdReplacements,
                  appendBlockIdReplacements
                );

                const appendedDocs = [targetDoc, ...systemDocs];
                await registerImportedMarketplaceDatabaseSystemDocAccess(
                  workspaceId,
                  appendedDocs,
                  controller.signal
                );

                for (const importedDoc of importedPageDocs) {
                  collection.removeDoc(importedDoc.id);
                }
                await discardMarketplaceImportPlaceholders({
                  workspaceId,
                  docIds: [...importedPageDocIds],
                  signal: controller.signal,
                });

                keptDocs = appendedDocs;
                keptImportedDocs = keptDocs;
                for (const doc of systemDocs) {
                  cleanupImportedDocIdsAfterBackendFailure.add(doc.id);
                }
                userVisibleDocs = [targetDoc];
                installedDocIds = [targetDoc.id];
              } finally {
                targetRelease();
              }
            } catch (error) {
              const appendCleanupDocIds = [
                ...importedPageDocIds,
                ...systemDocs.map(doc => doc.id),
              ];
              for (const doc of [...systemDocs, ...importedPageDocs]) {
                workspace.docCollection.removeDoc(doc.id);
              }
              await discardMarketplaceImportPlaceholders({
                workspaceId,
                docIds: appendCleanupDocIds,
                signal: controller.signal,
              }).catch(discardError => {
                console.warn(
                  '[Marketplace] Failed to discard temporary import docs after append error',
                  discardError
                );
              });
              throw error;
            }
          } else {
            keptImportedDocs = keptDocs;
            for (const doc of keptDocs) {
              cleanupImportedDocIdsAfterBackendFailure.add(doc.id);
            }
            installedDocIds = keptDocs.map((d: Store) =>
              normalizeImportedDatabaseDocId(d.id, databaseDocIdReplacements)
            );
            syncImportedUserVisibleDocMetadata(docsService, userVisibleDocs);

            await waitForImportedDocsServerConfirmed(
              workspace,
              docsService,
              userVisibleDocs
            );
            await registerImportedMarketplaceDatabaseSystemDocAccess(
              workspaceId,
              keptDocs,
              controller.signal
            );
          }
        }

        setInstallStep('creating-workflow');

        if (templateSummary.templateType === 'app') {
          effectiveBindings = {
            ...(effectiveBindings ?? {
              credentialMappings: {},
              configValues: {},
            }),
            appImportOptions: {
              docIdReplacements: Object.fromEntries(importDocIdReplacements),
              blockIdReplacements: Object.fromEntries(
                importBlockIdReplacements
              ),
            },
          };
        }
        const artifactTargets = buildArtifactTargets(
          keptImportedDocs,
          importBlockIdReplacements,
          importDocIdReplacements
        );
        if (artifactTargets.length > 0) {
          effectiveBindings = {
            ...(effectiveBindings ?? {
              credentialMappings: {},
              configValues: {},
            }),
            artifactTargets,
          };
        }
        const backendInstallOptions = installOptions
          ? {
              ...(installOptions.targetDocId
                ? { targetDocId: installOptions.targetDocId }
                : {}),
              ...(installOptions.createInputDbs !== undefined
                ? { createInputDbs: installOptions.createInputDbs }
                : {}),
              ...(installOptions.createOutputDbs !== undefined
                ? { createOutputDbs: installOptions.createOutputDbs }
                : {}),
              ...(installOptions.databaseBindings
                ? { databaseBindings: installOptions.databaseBindings }
                : {}),
            }
          : undefined;

        const completeInstallUrl = actionOptions?.replaceInstallId
          ? `/api/workspace/${workspaceId}/marketplace/installs/${actionOptions.replaceInstallId}/reinstall`
          : `/api/workspace/${workspaceId}/marketplace/complete-install/${templateId}`;
        const installRequestId = globalThis.crypto.randomUUID();
        if (!actionOptions?.replaceInstallId) {
          rememberPendingCompositeInstall({
            workspaceId,
            templateId,
            installRequestId,
            createdAt: new Date().toISOString(),
          });
        }

        // 4. Record install + create workflow engine workflow via backend.
        //    If bindings provided (from ImportSetupWizard), send them for
        //    concretization.
        const completeBody = {
          installRequestId,
          installedDocIds,
          ...(effectiveBindings ? { bindings: effectiveBindings } : {}),
          ...(installOptions?.targetVersionNumber != null
            ? { targetVersionNumber: installOptions.targetVersionNumber }
            : {}),
          ...(backendInstallOptions &&
          Object.keys(backendInstallOptions).length > 0
            ? { installOptions: backendInstallOptions }
            : {}),
        };
        const completeRes = actionOptions?.replaceInstallId
          ? await workspaceApiFetch(
              completeInstallUrl,
              {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(completeBody),
                signal: controller.signal,
              },
              { workspaceId, mode: 'foreground' }
            )
          : await completeInstallWithRecovery({
              url: completeInstallUrl,
              workspaceId,
              installRequestId,
              body: completeBody,
              signal: controller.signal,
              fetcher: workspaceApiFetch,
            });

        let workflowId: string | null = null;
        let templateTitle = '';
        let credentialRequirements: CredentialRequirement[] = [];
        let appInstall: InstallResult['appInstall'];
        let artifactBindings: InstallResult['artifactBindings'];
        let installId: string | undefined;

        if (completeRes.ok) {
          const completeResult = await completeRes.json();
          backendInstallRecorded = true;
          workflowId = completeResult.workflowId;
          templateTitle = completeResult.templateTitle;
          credentialRequirements = completeResult.credentialRequirements ?? [];
          appInstall = completeResult.appInstall;
          artifactBindings = completeResult.artifactBindings;
          installId = completeResult.installId;
          committedInstallId = installId ?? null;
          if (artifactBindings?.bindings.length) {
            setInstallStep('finalizing');
            wireInstalledAutomationArtifactBindings(
              keptImportedDocs,
              artifactBindings.bindings
            );
            await flushMarketplaceInstalledDocs(workspace, keptImportedDocs);
          }
          if (templateSummary.templateType === 'automation') {
            if (typeof workflowId !== 'string' || !workflowId) {
              throw new Error(
                'Automation install did not return a workflow id'
              );
            }
            setInstallStep('finalizing');
            wireInstalledAutomationBlocks(keptImportedDocs, {
              workflowId,
              docIdReplacements,
              databaseBindings: resolvedDatabaseBindings,
              automationBlockIds: automationBlockIdsToWire,
            });
            await flushMarketplaceInstalledDocs(workspace, keptImportedDocs);
          }
          if (
            templateSummary.templateType === 'app' &&
            appInstall?.automationBindings?.length &&
            !artifactBindings?.bindings.length
          ) {
            setInstallStep('finalizing');
            for (const binding of appInstall.automationBindings) {
              wireInstalledAutomationBlocks(keptImportedDocs, {
                workflowId: binding.workflowId,
                docIdReplacements: importDocIdReplacements,
                automationBlockIds: [binding.blockId],
              });
            }
            await flushMarketplaceInstalledDocs(workspace, keptImportedDocs);
          }
        } else if (completeRes.status === 409) {
          // Step templates are workspace capabilities. The backend rejects
          // adding the same managed step twice; workflows and automations are
          // repeatable blueprints and do not use this path.
          const body = await completeRes.json().catch(() => ({}));
          const existing =
            body?.message?.existingInstall ?? body?.existingInstall;
          throw Object.assign(
            new Error(
              typeof body?.message === 'string'
                ? body.message
                : 'Step is already available in this workspace'
            ),
            { code: 'DUPLICATE_INSTALL', existingInstall: existing }
          );
        } else {
          // sl-mkx.6-rework: fail loud on every non-409 backend rejection
          // (422 missing bindings, 500 concretization failure, etc.).
          // Previously these fell through to the success path with
          // `workflowId=null` and advanced the wizard to `done`, which
          // showed "installed" UI for a rejected install and left the
          // imported docs orphaned in the workspace.
          const body = await completeRes.json().catch(() => ({}));
          throw createMarketplaceActionError(
            body,
            `Failed to complete install: ${completeRes.status}`
          );
        }

        const result: InstallResult = {
          installId,
          installedDocIds,
          workflowId,
          templateTitle,
          credentialRequirements,
          ...(appInstall ? { appInstall } : {}),
          ...(artifactBindings ? { artifactBindings } : {}),
        };

        setInstallStep('done');
        setInstallResult(result);
        publishMarketplaceInstallChange({
          kind: actionOptions?.replaceInstallId ? 'updated' : 'installed',
          workspaceId,
          templateId,
        });
        if (!actionOptions?.replaceInstallId) {
          forgetPendingCompositeInstall(workspaceId);
        }
        return result;
      } catch (err: any) {
        if (backendInstallRecorded && committedInstallId) {
          const abortController = new AbortController();
          const abortResponse = await workspaceApiFetch(
            `/api/workspace/${workspaceId}/marketplace/installs/${committedInstallId}/abort`,
            {
              method: 'POST',
              credentials: 'include',
              signal: abortController.signal,
            }
          ).catch(() => null);
          if (abortResponse?.ok) {
            backendInstallRecorded = false;
            forgetPendingCompositeInstall(workspaceId);
          }
        }
        if (err.name !== 'AbortError') {
          if (
            !backendInstallRecorded &&
            cleanupImportedDocIdsAfterBackendFailure.size > 0
          ) {
            const cleanupDocIds = [
              ...cleanupImportedDocIdsAfterBackendFailure,
            ].filter(docId => docId !== installOptions?.targetDocId);
            await Promise.all(
              cleanupDocIds.map(docId =>
                docsService.discardLocalDoc(docId).catch(cleanupError => {
                  console.warn(
                    '[Marketplace] Failed to discard imported local doc after backend install rejection',
                    cleanupError
                  );
                })
              )
            );
            await discardMarketplaceImportPlaceholders({
              workspaceId,
              docIds: cleanupDocIds.filter(isMarketplaceSystemDocId),
              signal: controller.signal,
            }).catch(cleanupError => {
              console.warn(
                '[Marketplace] Failed to clean imported docs after backend install rejection',
                cleanupError
              );
            });
          }
          setInstallStep('error');
          setInstallError(toInstallErrorMessage(err, t));
          if (actionOptions?.propagateError) {
            throw err;
          }
        }
        return null;
      }
    },
    [workspaceId, workspace, docsService, guardService, t]
  );

  const resetInstall = useCallback(() => {
    installAbortRef.current?.abort();
    setInstallStep(null);
    setInstallResult(null);
    setInstallError(null);
  }, []);

  const reinstallTemplate = useCallback(
    async (args: {
      installId: string;
      templateId: string;
      bindings?: ConcretizationBindings;
      targetVersionNumber?: number;
      templateSummary?: InstallableTemplateSummary;
    }): Promise<InstallResult | null> =>
      installTemplate(
        args.templateId,
        'stay',
        args.bindings,
        args.targetVersionNumber != null
          ? { targetVersionNumber: args.targetVersionNumber }
          : undefined,
        args.templateSummary,
        { replaceInstallId: args.installId, propagateError: true }
      ),
    [installTemplate]
  );

  const uninstallTemplate = useCallback(
    async (installId: string): Promise<boolean> => {
      if (!workspaceId) return false;

      setUninstallingId(installId);
      setUninstallError(null);
      try {
        const res = await workspaceApiFetch(
          `/api/workspace/${workspaceId}/marketplace/installs/${installId}`,
          { method: 'DELETE', credentials: 'include' }
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message ?? `Uninstall failed: ${res.status}`);
        }
        publishMarketplaceInstallChange({
          kind: 'removed',
          workspaceId,
        });
        return true;
      } catch (err) {
        setUninstallError(
          err instanceof Error ? err.message : 'Uninstall failed'
        );
        return false;
      } finally {
        setUninstallingId(null);
      }
    },
    [workspaceId]
  );

  // Rate
  const rateTemplate = useCallback(
    async (
      templateId: string,
      rating: number,
      comment?: string
    ): Promise<RatingResult> => {
      const res = await fetch(`/api/marketplace/templates/${templateId}/rate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, comment: comment || undefined }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? `Rating failed: ${res.status}`);
      }

      return (await res.json()) as RatingResult;
    },
    []
  );

  // sl-mkx.2.3: Update install to a newer template version in-place
  const updateInstall = useCallback(
    async (args: {
      installId: string;
      targetVersionNumber?: number;
      bindings?: ConcretizationBindings;
    }): Promise<{
      templateId: string;
      fromVersion: number;
      toVersion: number;
      workflowId: string | null;
    } | null> => {
      if (!workspaceId) return null;
      const res = await workspaceApiFetch(
        `/api/workspace/${workspaceId}/marketplace/installs/${args.installId}/update`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...(args.targetVersionNumber != null
              ? { targetVersionNumber: args.targetVersionNumber }
              : {}),
            ...(args.bindings ? { bindings: args.bindings } : {}),
          }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw createMarketplaceActionError(
          body,
          `Update failed: ${res.status}`
        );
      }
      return await res.json();
    },
    [workspaceId]
  );

  // Report
  const reportTemplate = useCallback(
    async (
      templateId: string,
      reason: string,
      details?: string
    ): Promise<ReportResult | null> => {
      try {
        const res = await fetch(
          `/api/marketplace/templates/${templateId}/report`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reason, details: details || undefined }),
          }
        );

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message ?? `Report failed: ${res.status}`);
        }

        return await res.json();
      } catch {
        return null;
      }
    },
    []
  );

  return {
    // Install
    installTemplate,
    installStep,
    installResult,
    installError,
    resetInstall,
    reinstallTemplate,
    // Uninstall
    uninstallTemplate,
    uninstallingId,
    uninstallError,
    // Update
    updateInstall,
    // Rate
    rateTemplate,
    // Report
    reportTemplate,
  };
}

function isMarketplaceSystemDocId(docId: string): boolean {
  return docId.startsWith('db$');
}

function normalizeImportedDocIdReplacementValues(
  docIdReplacements: Map<string, string>,
  databaseDocIdReplacements: ReadonlyMap<string, string>
): void {
  if (databaseDocIdReplacements.size === 0) return;

  for (const [sourceDocId, targetDocId] of docIdReplacements) {
    docIdReplacements.set(
      sourceDocId,
      normalizeImportedDatabaseDocId(targetDocId, databaseDocIdReplacements)
    );
  }
}

function normalizeImportedDatabaseDocId(
  docId: string,
  databaseDocIdReplacements: ReadonlyMap<string, string>
): string {
  return databaseDocIdReplacements.get(docId) ?? docId;
}

function composeReplacementMap(
  originalToIntermediate: Map<string, string>,
  intermediateToTarget: ReadonlyMap<string, string>
): void {
  for (const [source, intermediate] of originalToIntermediate) {
    const target = intermediateToTarget.get(intermediate);
    if (target) originalToIntermediate.set(source, target);
  }
}

function buildArtifactTargets(
  docs: readonly Store[],
  blockIdReplacements: ReadonlyMap<string, string>,
  docIdReplacements: ReadonlyMap<string, string>
): NonNullable<ConcretizationBindings['artifactTargets']> {
  const targets: NonNullable<ConcretizationBindings['artifactTargets']> = [];
  const seenTargets = new Set<string>();
  for (const [sourceBlockId, targetBlockId] of blockIdReplacements) {
    const matchingDocs = docs.filter(doc => {
      // A database host block and its db$ storage root intentionally share the
      // remapped database id. Artifact bindings point at the visible host.
      if (isMarketplaceSystemDocId(doc.id)) return false;
      const flavour = doc.getBlock(targetBlockId)?.model.flavour;
      return flavour === 'affine:automation' || flavour === 'affine:database';
    });
    if (matchingDocs.length === 0) continue;
    if (matchingDocs.length !== 1) {
      throw new Error(
        `Imported block ${targetBlockId} must belong to exactly one document`
      );
    }
    const targetDocId = matchingDocs[0]!.id;
    const sourceDocId = [...docIdReplacements].find(
      ([, target]) => target === targetDocId
    )?.[0];
    const targetKey = `${targetDocId}:${targetBlockId}`;
    if (seenTargets.has(targetKey)) {
      throw new Error(`Imported block target is duplicated: ${targetKey}`);
    }
    seenTargets.add(targetKey);
    const flavour = matchingDocs[0]!.getBlock(targetBlockId)!.model.flavour;
    targets.push({
      sourceDocId,
      sourceBlockId,
      targetDocId,
      targetBlockId,
      ...(flavour === 'affine:database' && sourceDocId
        ? { resourceKey: `database:${sourceDocId}:${sourceBlockId}` }
        : {}),
    });
  }
  return targets;
}
